/**
 * Client HTTP de l'API AGORA : jeton d'ACCÈS Cognito (l'API refuse l'id_token), erreurs RFC 9457 (Probleme), 401 → rafraîchissement puis reconnexion.
 * Les écrans l'utilisent via le hook useApi() du socle (web/src/socle).
 */
import { CODES_ERREUR, type CodeErreur, type Probleme } from '@agora/shared';

export type CodeErreurFront = CodeErreur | 'reseau';

/** Erreur d'API : `code` stable → libellé `erreurs.<code>` ; `champs` = erreurs de validation par champ. */
export class ErreurApi extends Error {
  constructor(public code: CodeErreurFront, public status: number, public detail?: string, public champs?: Record<string, string>) {
    super(detail || code);
  }
}

export type Requete = { methode?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; corps?: unknown; query?: Record<string, string | number | undefined | null>; signal?: AbortSignal };

export interface ClientApi {
  requete<T>(chemin: string, r?: Requete): Promise<T>;
  get<T>(chemin: string, query?: Requete['query'], signal?: AbortSignal): Promise<T>;
  post<T>(chemin: string, corps: unknown): Promise<T>;
  put<T>(chemin: string, corps: unknown): Promise<T>;
  patch<T>(chemin: string, corps: unknown): Promise<T>;
  supprimer<T>(chemin: string): Promise<T>;
}

export type Transport = (methode: string, chemin: string, corps: unknown) => Promise<{ status: number; json: unknown }>;

function codeDepuisStatut(status: number): CodeErreur {
  if (status === 401) return 'non_authentifie';
  if (status === 403) return 'interdit';
  if (status === 404) return 'introuvable';
  if (status === 409) return 'conflit';
  if (status === 400 || status === 422) return 'validation';
  if (status === 503) return 'base_indisponible';
  return 'interne';
}

/** Code renvoyé par l'API s'il figure dans CODES_ERREUR (liste partagée : tout nouveau code passe), sinon déduit du statut. */
function erreurDepuisReponse(status: number, json: unknown): ErreurApi {
  const p = (json || {}) as Partial<Probleme>;
  const code = p.code && CODES_ERREUR.includes(p.code) ? p.code : codeDepuisStatut(status);
  return new ErreurApi(code, status, p.detail || p.title, p.champs);
}

export function construireChemin(chemin: string, query?: Requete['query']): string {
  if (!query) return chemin;
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `${chemin}${chemin.includes('?') ? '&' : '?'}${s}` : chemin;
}

/**
 * @param base         URL de l'API (ConfigFront.apiUrl)
 * @param jeton        fournit le jeton d'accès (forcer = rafraîchir)
 * @param surPerte     appelé quand la session est perdue (401 après rafraîchissement)
 * @param langue       langue de l'utilisateur (Accept-Language)
 * @param transport    transport de remplacement (mode maquette)
 */
export function creerClient(base: string, jeton: (forcer?: boolean) => Promise<string | null>, surPerte: () => void, langue: () => string, transport?: Transport): ClientApi {
  async function requete<T>(chemin: string, r: Requete = {}): Promise<T> {
    const methode = r.methode || 'GET';
    const url = construireChemin(chemin, r.query);
    if (transport) {
      const { status, json } = await transport(methode, url, r.corps);
      if (status >= 400) throw erreurDepuisReponse(status, json);
      return json as T;
    }
    const essai = async (forcer: boolean): Promise<Response> => {
      // Rafraîchissement reporté (réseau coupé, Cognito indisponible) : erreur réseau, la session reste ouverte.
      const j = await jeton(forcer).catch(() => { throw new ErreurApi('reseau', 0); });
      if (!j) throw new ErreurApi('non_authentifie', 401);
      try {
        return await fetch(base + url, {
          method: methode, signal: r.signal,
          headers: {
            Authorization: `Bearer ${j}`, Accept: 'application/json', 'Accept-Language': langue(),
            ...(r.corps !== undefined ? { 'Content-Type': 'application/json' } : {}),
          },
          body: r.corps !== undefined ? JSON.stringify(r.corps) : undefined,
        });
      } catch (e) {
        if ((e as Error).name === 'AbortError') throw e;
        throw new ErreurApi('reseau', 0);
      }
    };
    let rep = await essai(false);
    if (rep.status === 401) {
      rep = await essai(true).catch((e) => { throw e; });
      if (rep.status === 401) { surPerte(); throw new ErreurApi('non_authentifie', 401); }
    }
    const texte = await rep.text();
    const json = texte ? (() => { try { return JSON.parse(texte); } catch { return undefined; } })() : undefined;
    if (!rep.ok) throw erreurDepuisReponse(rep.status, json);
    return json as T;
  }
  return {
    requete,
    get: (c, query, signal) => requete(c, { query, signal }),
    post: (c, corps) => requete(c, { methode: 'POST', corps }),
    put: (c, corps) => requete(c, { methode: 'PUT', corps }),
    patch: (c, corps) => requete(c, { methode: 'PATCH', corps }),
    supprimer: (c) => requete(c, { methode: 'DELETE' }),
  };
}
